// Alternative approach using Aladin's global click handler
// Updated ConeSearchPanel.jsx - Using Aladin click events instead of catalog events

import React, { useState, useRef, useEffect, useCallback } from 'react';
import astroApi from '../../api/astro';

const ConeSearchPanel = ({
  show,
  setShow,
  aladinInstance,
  selectedCoords,
  setSelectedCoords,
  setSelectedObject,
  tables
}) => {
  const [selectedTable, setSelectedTable] = useState('');
  const [searchRadius, setSearchRadius] = useState(0.1);
  const [searchLimit, setSearchLimit] = useState(100);
  const [coneSearchResults, setConeSearchResults] = useState(null);
  const [coneSearchLoading, setConeSearchLoading] = useState(false);
  const [coneSearchError, setConeSearchError] = useState(null);
  
  const catalogOverlay = useRef(null);
  const coneOverlay = useRef(null);
  const coordinateUpdateInterval = useRef(null);
  const searchResultsRef = useRef(null);
  const clickHandlerAttached = useRef(false);

  // Set default table when tables load
  useEffect(() => {
    if (tables && tables.length > 0 && !selectedTable) {
      setSelectedTable(`${tables[0].schema_name}.${tables[0].table_name}`);
    }
  }, [tables, selectedTable]);

  // Update coordinates function
  const updateCoordinates = useCallback(() => {
    if (!aladinInstance.current || !show) return;

    try {
      const center = aladinInstance.current.getRaDec();
      if (center && typeof center[0] === 'number' && typeof center[1] === 'number') {
        const coords = { ra: center[0], dec: center[1] };
        
        if (!selectedCoords || 
            Math.abs(selectedCoords.ra - coords.ra) > 0.000001 || 
            Math.abs(selectedCoords.dec - coords.dec) > 0.000001) {
          
          setSelectedCoords(coords);
          // Only update selectedObject if it's a position type (preserve catalog selections)
          setSelectedObject(prev => {
            if (!prev || prev.__type === 'position') {
              return {
                __type: 'position',
                ra: coords.ra,
                dec: coords.dec,
                note: 'Center crosshair position for cone search',
              };
            }
            return prev;
          });
          drawConeOverlay(coords.ra, coords.dec, searchRadius);
        }
      }
    } catch (e) {
      console.error('Error updating coordinates:', e);
    }
  }, [show, aladinInstance, setSelectedCoords, setSelectedObject, searchRadius, selectedCoords]);

  // Attach global click handler to Aladin
  const attachGlobalClickHandler = useCallback(() => {
    if (!aladinInstance.current || clickHandlerAttached.current) return;

    try {
      // Remove any existing handlers
      if (aladinInstance.current.off) {
        aladinInstance.current.off('objectClicked');
        aladinInstance.current.off('click');
      }

      // Attach object click handler
      if (aladinInstance.current.on) {
        aladinInstance.current.on('objectClicked', (object) => {
          console.log('Object clicked via objectClicked event:', object);
          
          if (object && searchResultsRef.current) {
            try {
              // Find the clicked object in our search results
              const results = searchResultsRef.current;
              const clickedObj = results.results.find(obj => {
                const objRA = obj[results.metadata.ra_column];
                const objDEC = obj[results.metadata.dec_column];
                return Math.abs(objRA - object.ra) < 0.0001 && Math.abs(objDEC - object.dec) < 0.0001;
              });

              if (clickedObj) {
                console.log('Found clicked object in results:', clickedObj);
                setSelectedObject({
                  __type: 'catalog',
                  __ra: object.ra,
                  __dec: object.dec,
                  __catalog_name: selectedTable,
                  __search_radius: searchRadius,
                  __metadata: results.metadata,
                  ...clickedObj
                });
              } else {
                console.log('Object clicked but not found in search results');
                setSelectedObject({
                  __type: 'catalog',
                  __ra: object.ra,
                  __dec: object.dec,
                  __catalog_name: selectedTable,
                  __metadata: results.metadata,
                  note: 'Object clicked but details not available',
                });
              }
            } catch (error) {
              console.error('Error handling object click:', error);
            }
          }
        });

        // Fallback: general click handler
        aladinInstance.current.on('click', (clickData) => {
          console.log('General click event:', clickData);
          
          // Only process if we have search results and the click is near an object
          if (searchResultsRef.current && clickData.ra && clickData.dec) {
            const results = searchResultsRef.current;
            
            // Find nearest object within a small radius
            let nearestObj = null;
            let minDistance = Infinity;
            const clickThreshold = 0.001; // degrees
            
            results.results.forEach(obj => {
              const objRA = obj[results.metadata.ra_column];
              const objDEC = obj[results.metadata.dec_column];
              const distance = Math.sqrt(
                Math.pow(objRA - clickData.ra, 2) + 
                Math.pow(objDEC - clickData.dec, 2)
              );
              
              if (distance < clickThreshold && distance < minDistance) {
                nearestObj = obj;
                minDistance = distance;
              }
            });
            
            if (nearestObj) {
              console.log('Found nearest object via general click:', nearestObj);
              setSelectedObject({
                __type: 'catalog',
                __ra: clickData.ra,
                __dec: clickData.dec,
                __catalog_name: selectedTable,
                __search_radius: searchRadius,
                __metadata: results.metadata,
                ...nearestObj
              });
            }
          }
        });

        clickHandlerAttached.current = true;
        console.log('Global click handlers attached');
      }
    } catch (error) {
      console.error('Error attaching global click handler:', error);
    }
  }, [aladinInstance, searchRadius, selectedTable, setSelectedObject]);

  // Detach global click handler
  const detachGlobalClickHandler = useCallback(() => {
    if (aladinInstance.current && clickHandlerAttached.current) {
      try {
        if (aladinInstance.current.off) {
          aladinInstance.current.off('objectClicked');
          aladinInstance.current.off('click');
        }
        clickHandlerAttached.current = false;
        console.log('Global click handlers detached');
      } catch (error) {
        console.error('Error detaching global click handler:', error);
      }
    }
  }, [aladinInstance]);

  // Start auto-updating coordinates when cone search is activated
  useEffect(() => {
    if (show && aladinInstance.current) {
      updateCoordinates();
      coordinateUpdateInterval.current = setInterval(updateCoordinates, 250);
      attachGlobalClickHandler();
      
      return () => {
        if (coordinateUpdateInterval.current) {
          clearInterval(coordinateUpdateInterval.current);
          coordinateUpdateInterval.current = null;
        }
      };
    } else {
      if (coordinateUpdateInterval.current) {
        clearInterval(coordinateUpdateInterval.current);
        coordinateUpdateInterval.current = null;
      }
      detachGlobalClickHandler();
    }
  }, [show, updateCoordinates, attachGlobalClickHandler, detachGlobalClickHandler]);

  // Update cone overlay when radius changes
  useEffect(() => {
    if (show && selectedCoords) {
      drawConeOverlay(selectedCoords.ra, selectedCoords.dec, searchRadius);
    }
  }, [searchRadius, show, selectedCoords]);

  // Draw cone overlay on Aladin
  const drawConeOverlay = (ra, dec, radiusDeg) => {
    if (!aladinInstance.current || !window.A) return;

    try {
      if (coneOverlay.current) {
        aladinInstance.current.removeCatalog &&
          aladinInstance.current.removeCatalog(coneOverlay.current);
      }

      coneOverlay.current = window.A.catalog({
        name: 'Cone Search Area',
        sourceSize: 0,
        color: 'cyan',
        shape: 'circle',
      });

      const circle = window.A.circle(ra, dec, radiusDeg, {
        color: 'cyan',
        lineWidth: 2,
        fill: false
      });

      if (aladinInstance.current.addOverlay) {
        aladinInstance.current.addOverlay(circle);
      } else {
        const source = window.A.source(ra, dec, { 
          cone_radius: radiusDeg,
          cone_center: true 
        });
        coneOverlay.current.addSources([source]);
      }

      aladinInstance.current.addCatalog(coneOverlay.current);
    } catch (e) {
      console.error('Error drawing cone overlay:', e);
    }
  };

  // Manual update coordinates to current center
  const manualUpdateCoordinates = () => {
    updateCoordinates();
  };

  // Plot results on Aladin - simplified without catalog click handlers
  const plotResultsOnAladin = (results) => {
    if (!aladinInstance.current || !results || !results.results || !results.results.length || !window.A) {
      return;
    }

    console.log('Plotting results:', results);

    try {
      if (catalogOverlay.current) {
        aladinInstance.current.removeCatalog &&
          aladinInstance.current.removeCatalog(catalogOverlay.current);
      }

      // Store results for click handler
      searchResultsRef.current = results;

      const catalog = window.A.catalog({
        name: 'Cone Search Results',
        sourceSize: 12, // Larger for easier clicking
        color: 'red',
        shape: 'circle',
      });

      // Add sources - simplified without click handlers
      results.results.forEach((obj, index) => {
        const ra = obj[results.metadata.ra_column];
        const dec = obj[results.metadata.dec_column];
        if (typeof ra === 'number' && typeof dec === 'number') {
          const source = window.A.source(ra, dec, {
            objectIndex: index, // Simple identifier
          });
          catalog.addSources([source]);
        }
      });

      aladinInstance.current.addCatalog(catalog);
      catalogOverlay.current = catalog;
      
      console.log('Successfully plotted', results.results.length, 'objects using global click handler');
    } catch (e) {
      console.error('Error plotting results on Aladin:', e);
    }
  };

  // Perform cone search
  const performConeSearch = async () => {
    if (!selectedCoords || !selectedTable) return;

    try {
      setConeSearchLoading(true);
      setConeSearchError(null);
      console.log('Performing cone search...');

      const results = await astroApi.performConeSearch(
        selectedTable,
        selectedCoords.ra,
        selectedCoords.dec,
        searchRadius,
        searchLimit
      );

      console.log('Cone search results:', results);
      setConeSearchResults(results);
      plotResultsOnAladin(results);
    } catch (e) {
      console.error('Cone search error:', e);
      setConeSearchError((e && e.message) || 'Failed to perform cone search');
    } finally {
      setConeSearchLoading(false);
    }
  };

  // Clear cone search results
  const clearConeSearchResults = () => {
    console.log('Clearing cone search results');
    setConeSearchResults(null);
    setConeSearchError(null);
    searchResultsRef.current = null;
    
    try {
      if (catalogOverlay.current && aladinInstance.current) {
        aladinInstance.current.removeCatalog &&
          aladinInstance.current.removeCatalog(catalogOverlay.current);
        catalogOverlay.current = null;
      }
    } catch (e) {
      console.error('Error removing catalog overlay:', e);
    }
  };

  // Clean up overlays when panel is closed
  useEffect(() => {
    if (!show) {
      try {
        if (catalogOverlay.current && aladinInstance.current) {
          aladinInstance.current.removeCatalog &&
            aladinInstance.current.removeCatalog(catalogOverlay.current);
          catalogOverlay.current = null;
        }
        if (coneOverlay.current && aladinInstance.current) {
          aladinInstance.current.removeCatalog &&
            aladinInstance.current.removeCatalog(coneOverlay.current);
          coneOverlay.current = null;
        }
      } catch (e) {
        console.error('Error cleaning up overlays:', e);
      }
      setConeSearchResults(null);
      setSelectedCoords(null);
      setSelectedObject(null);
      setConeSearchError(null);
      searchResultsRef.current = null;
    }
  }, [show, setSelectedCoords, setSelectedObject]);

  if (!show) return null;

  return (
    <div className="absolute top-20 right-0 bottom-0 w-96 bg-ui-light-card/85 dark:bg-ui-dark-card/85 backdrop-blur-sm border-l border-ui-light-border/60 dark:border-ui-dark-border/60 shadow-lg transition-all duration-300 ease-in-out z-30">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-ui-light-border/50 dark:border-ui-dark-border/50 bg-gradient-to-r from-primary-50/80 to-primary-100/80 dark:from-primary-900/40 dark:to-primary-800/40 backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-600/90 dark:bg-primary-700/90 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
                <circle cx="11" cy="11" r="3"></circle>
              </svg>
            </div>
            <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Cone Search</h2>
          </div>
          <button
            onClick={() => setShow(false)}
            className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:text-ui-light-text-primary dark:hover:text-ui-dark-text-primary transition-colors p-1 hover:bg-ui-light-hover/70 dark:hover:bg-ui-dark-hover/70 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 text-ui-light-text-primary dark:text-ui-dark-text-primary">
          {/* Instructions */}
          <div className="mb-6 p-4 bg-primary-50/70 dark:bg-primary-900/40 border border-primary-200/60 dark:border-primary-800/60 rounded-lg backdrop-blur-sm">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-primary-700 dark:text-primary-300 mb-1">Global click handler:</p>
                <p className="text-primary-600 dark:text-primary-400 text-sm">
                  Using Aladin's global events. Click red objects to see details. Check console for click events.
                </p>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {coneSearchError && (
            <div className="mb-4 p-3 bg-status-error-light-bg/80 dark:bg-status-error-dark-bg/80 border border-status-error-light-text/30 dark:border-status-error-dark-text/30 rounded-lg backdrop-blur-sm">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-status-error-light-text dark:text-status-error-dark-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-status-error-light-text dark:text-status-error-dark-text text-sm font-medium">Error:</p>
              </div>
              <p className="text-status-error-light-text dark:text-status-error-dark-text text-sm mt-1">{coneSearchError}</p>
            </div>
          )}

          {/* Debug Info */}
          {coneSearchResults && (
            <div className="mb-4 p-3 bg-gray-100/80 dark:bg-gray-800/80 border border-gray-300/50 dark:border-gray-600/50 rounded-lg backdrop-blur-sm">
              <p className="text-xs font-mono text-gray-600 dark:text-gray-400">
                Debug: {coneSearchResults.results.length} objects loaded with global click handler
              </p>
            </div>
          )}

          {/* Rest of the form remains the same... */}
          <div className="space-y-6">
            {/* Table Selection */}
            <div>
              <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-2">
                Astronomical Catalog
              </label>
              <select
                value={selectedTable}
                onChange={(e) => setSelectedTable(e.target.value)}
                className="w-full p-3 bg-ui-light-background/80 dark:bg-ui-dark-background/80 backdrop-blur-sm border border-ui-light-border/60 dark:border-ui-dark-border/60 rounded-lg text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/60 focus:border-transparent transition-colors"
                disabled={coneSearchLoading}
              >
                <option value="">Select a catalog...</option>
                {tables.map((table) => (
                  <option
                    key={`${table.schema_name}.${table.table_name}`}
                    value={`${table.schema_name}.${table.table_name}`}
                  >
                    {table.display_name || `${table.schema_name}.${table.table_name}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Coordinates Display */}
            <div>
              <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-2">
                Target Coordinates (Auto-updating)
              </label>
              <div className="p-4 bg-ui-light-background/70 dark:bg-ui-dark-background/70 backdrop-blur-sm border border-ui-light-border/50 dark:border-ui-dark-border/50 rounded-lg">
                {selectedCoords ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-cyan-500 rounded-full animate-pulse"></div>
                        <div className="text-sm">
                          <div className="font-mono">
                            <span className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">RA:</span> {selectedCoords.ra.toFixed(6)}°
                          </div>
                          <div className="font-mono">
                            <span className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Dec:</span> {selectedCoords.dec.toFixed(6)}°
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={manualUpdateCoordinates}
                        className="px-2 py-1 text-xs bg-primary-600/80 hover:bg-primary-700 text-white rounded transition-colors"
                        title="Force update coordinates"
                      >
                        Refresh
                      </button>
                    </div>
                    <p className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                      Updates automatically every 250ms
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center space-x-3 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                    <div className="w-3 h-3 border-2 border-dashed border-ui-light-text-tertiary dark:border-ui-dark-text-tertiary rounded-full animate-spin"></div>
                    <span className="text-sm italic">Loading coordinates...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Search Parameters */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-2">
                  Radius (°)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.001"
                  max="1"
                  value={searchRadius}
                  onChange={(e) => setSearchRadius(parseFloat(e.target.value) || 0.1)}
                  className="w-full p-3 bg-ui-light-background/80 dark:bg-ui-dark-background/80 backdrop-blur-sm border border-ui-light-border/60 dark:border-ui-dark-border/60 rounded-lg text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/60 focus:border-transparent transition-colors"
                  disabled={coneSearchLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-2">
                  Limit
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={searchLimit}
                  onChange={(e) => setSearchLimit(parseInt(e.target.value) || 100)}
                  className="w-full p-3 bg-ui-light-background/80 dark:bg-ui-dark-background/80 backdrop-blur-sm border border-ui-light-border/60 dark:border-ui-dark-border/60 rounded-lg text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/60 focus:border-transparent transition-colors"
                  disabled={coneSearchLoading}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={performConeSearch}
                disabled={!selectedCoords || !selectedTable || coneSearchLoading}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600/90 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500/60 transition-colors"
              >
                {coneSearchLoading ? (
                  <div className="flex items-center justify-center">
                    <svg className="w-4 h-4 text-white animate-spin mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Searching...
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                    <span>Search Objects</span>
                  </div>
                )}
              </button>

              <button
                onClick={clearConeSearchResults}
                className="w-full px-4 py-2 text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-hover/80 dark:bg-ui-dark-hover/80 hover:bg-ui-light-border/80 dark:hover:bg-ui-dark-border/80 rounded-md transition-colors border border-ui-light-border/50 dark:border-ui-dark-border/50 backdrop-blur-sm"
                disabled={coneSearchLoading}
              >
                Clear Results
              </button>
            </div>

            {/* Results Summary */}
            {coneSearchResults && (
              <div className="p-4 bg-status-success-light-bg/80 dark:bg-status-success-dark-bg/80 border border-status-success-light-text/30 dark:border-status-success-dark-text/30 rounded-lg backdrop-blur-sm">
                <div className="flex items-center space-x-2 mb-2">
                  <svg className="w-4 h-4 text-status-success-light-text dark:text-status-success-dark-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium text-status-success-light-text dark:text-status-success-dark-text">
                    Found {coneSearchResults.results.length} objects
                  </span>
                </div>
                <p className="text-status-success-light-text dark:text-status-success-dark-text text-sm">
                  Objects within {searchRadius}° are displayed as red dots. Click any red dot to view details in the panel below.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConeSearchPanel;